import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../context/ThemeContext.jsx";
import { useAuth } from "../../context/AuthContext.jsx";
import { fixImageUrl } from "../../utils/fixImageUrl.js";
import Search from "../SearchEngine/Search";
import NewsTicker from "./NewsTicker.jsx";
import backgroundImg from "../Images/backgroundImg.jpg";
import logoImg from "../Images/logo40.jpg";
import "./Header.css";

const MoonIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const SunIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

const UserIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const MenuIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { isDark, toggleTheme, toggleLanguage } = useTheme();
  const { isLoggedIn, logout, artist } = useAuth();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isHome = location.pathname === "/" || location.pathname === "/Home";
  const userMenuRef = useRef(null);

  useEffect(() => {
    const close = () => setMenuOpen(false);
    window.addEventListener("resize", close);
    return () => window.removeEventListener("resize", close);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (!e.target.closest(".spar-navbar")) setMenuOpen(false);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [menuOpen]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [userMenuOpen]);

  return (
    <header>
      {/* ── Sticky Navbar ── */}
      <nav
        className="spar-navbar"
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="spar-nav-inner">
          {/* Brand */}
          <a href="/Home" className="spar-brand" aria-label="SPAR Hub home">
            <img src={logoImg} alt="SPAR logo" />
            <span className="spar-brand-name">
              <span className="spar-brand-name-top">SPAR Hub</span>
              <span className="spar-brand-name-sub">
                Sudanese Plastic Artists Rally
              </span>
            </span>
          </a>

          {/* Nav Links */}
          <ul
            className={`spar-navlinks${menuOpen ? " open" : ""}`}
            role="menubar"
          >
            <li className="spar-nav-mobile-search" role="none">
              <Search />
            </li>
            <li role="none">
              <a href="/Home" role="menuitem">
                {t("nav.home")}
              </a>
            </li>
            <li role="none">
              <a href="/About-us" role="menuitem">
                {t("nav.about")}
              </a>
            </li>
            <li role="none">
              <a href="/Events" role="menuitem">
                {t("nav.news")}
              </a>
            </li>
            <li role="none">
              <a href="/ArtistsList" role="menuitem">
                {t("nav.artists")}
              </a>
            </li>
            <li role="none">
              <a href="/media" role="menuitem">
                {t("nav.media") || (i18n.language === "ar" ? "الإعلام والصحافة" : "Media & Press")}
              </a>
            </li>
            <li role="none">
              <a href="/gallery" role="menuitem">
                {t("nav.gallery")}
              </a>
            </li>

            <li className="spar-dropdown" role="none">
              <a
                href="#!"
                onClick={(e) => e.preventDefault()}
                role="menuitem"
                aria-haspopup="true"
              >
                {t("nav.action")} ▾
              </a>
              <div className="spar-dropdown-menu" role="menu">
                <a
                  href="https://round-city.com/sudanese-street-artists-give-khartoum-a-makeover/"
                  target="_blank"
                  rel="noreferrer"
                  role="menuitem"
                >
                  {t("nav.workshops")}
                </a>
                <a
                  href="https://storymaps.arcgis.com/stories/7f8b62d66c4243a9b93cd5656cd525f5"
                  target="_blank"
                  rel="noreferrer"
                  role="menuitem"
                >
                  {t("nav.streetArt")}
                </a>
                <a
                  href="https://www.facebook.com/spartripe"
                  target="_blank"
                  rel="noreferrer"
                  role="menuitem"
                >
                  {t("nav.mediaOffice")}
                </a>
              </div>
            </li>

            <li role="none">
              <a
                href="https://soundcloud.com/radio-dabanga/sets/todaytalk"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "var(--green)" }}
                role="menuitem"
              >
                {t("nav.radioVoice")}
              </a>
            </li>

            <li role="none">
              <a href="/Chat" role="menuitem" style={{ color: "var(--green)" }}>
                {t("nav.community")}
              </a>
            </li>

            <li role="none">
              <a href="/Forum" role="menuitem" style={{ color: "var(--teal)", fontWeight: 700 }}>
                {t("nav.forum")}
              </a>
            </li>

            {isLoggedIn ? (
              <li role="none">
                <a href="/Dashboard" role="menuitem">
                  {t("nav.dashboard")}
                </a>
              </li>
            ) : (
              <li className="spar-nav-cta" role="none">
                <a href="/signup" role="menuitem">
                  {t("nav.signup")}
                </a>
              </li>
            )}
          </ul>

          {/* Controls */}
          <div className="spar-nav-controls">
            <Search />

            <div className="spar-user-menu" ref={userMenuRef}>
              {isLoggedIn ? (
                <>
                  <button
                    className={`spar-user-avatar-btn${userMenuOpen ? " open" : ""}`}
                    onClick={() => setUserMenuOpen(o => !o)}
                    aria-label="Account menu"
                    aria-expanded={userMenuOpen}
                  >
                    {artist?.profileImage ? (
                      <img src={fixImageUrl(artist.profileImage)} alt={artist.name} className="spar-user-avatar-img" onError={(e) => { e.target.style.display = "none"; }} />
                    ) : (
                      <span className="spar-user-avatar-initial">{artist?.name?.[0]?.toUpperCase() || "?"}</span>
                    )}
                    <span className="spar-user-name">{artist?.name?.split(" ")[0]}</span>
                    <span className="spar-user-dot" />
                  </button>
                  {userMenuOpen && (
                    <div className="spar-user-dropdown">
                      <div className="spar-user-dropdown-header">
                        <span className="spar-user-dropdown-name">{artist?.name}</span>
                      </div>
                      <a href="/Dashboard" className="spar-user-dropdown-item" onClick={() => setUserMenuOpen(false)}>
                        <UserIcon /> {t("nav.dashboard")}
                      </a>
                      <button className="spar-user-dropdown-item danger" onClick={() => { logout(); setUserMenuOpen(false); }}>
                        ✕ {t("nav.logout")}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <button
                  className="spar-icon-btn"
                  onClick={() => window.location.href = "/login"}
                  title={t("nav.login")}
                  aria-label={t("nav.login")}
                >
                  <UserIcon />
                </button>
              )}
            </div>

            <button
              className="spar-icon-btn"
              onClick={toggleLanguage}
              title={t("lang.switch")}
              aria-label="Switch language"
            >
              {t("lang.switch")}
            </button>

            <button
              className="spar-icon-btn"
              onClick={toggleTheme}
              title={isDark ? t("theme.light") : t("theme.dark")}
              aria-label="Toggle colour theme"
            >
              {isDark ? <SunIcon /> : <MoonIcon />}
            </button>

            <button
              className="spar-icon-btn spar-hamburger"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
            >
              {menuOpen ? <CloseIcon /> : <MenuIcon />}
            </button>
          </div>
        </div>
      </nav>

      {/* ── News Ticker ── */}
      <NewsTicker />

      {/* ── Hero — home page only ── */}
      {isHome && (
        <div className="spar-hero" role="banner">
          <div
            className="spar-hero-bg"
            style={{ backgroundImage: `url(${backgroundImg})` }}
          />
          <div className="spar-hero-content">
            <div className="spar-hero-eyebrow">تجمع التشكيليين السودانيين</div>
            <h1 className="spar-hero-title">{t("hero.tagline")}</h1>
            <p className="spar-hero-subtitle">{t("hero.subtitle")}</p>
            <div className="spar-hero-actions">
              <a href="/ArtistsList" className="spar-btn spar-btn-primary">
                {t("hero.cta")}
              </a>
              <a href="/signup" className="spar-btn spar-btn-glass">
                {t("nav.signup")}
              </a>
            </div>
            <div className="spar-hero-contact">
              <span>+249 912 345 675</span>
              <span>+249 923 987 654</span>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
